import {Component, Inject, OnInit, ChangeDetectorRef, ChangeDetectionStrategy} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormGroup} from '@angular/forms';
import {ItemDataService} from '@emm/common-items/services/item-data.service';
import {VariationAssignment} from '@emm/common-items/models/variation.model';

@Component({
  selector: 'emm-create-assignment-dialog',
  templateUrl: './create-or-edit-assignment-dialog.component.html',
  styleUrls: ['./create-or-edit-assignment-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreateOrEditAssignmentDialogComponent implements OnInit{
    private static readonly DEFAULT_LOCATION = 'CHAIN';
    private static readonly DEFAULT_CONSUMER = 'COMMERCEPOINT';
    private static readonly LOCATION_LABELS: { [key: string]: string } = {
        CHAIN: 'Chain Wide Defaults',
        US: 'United States',
        CA: 'Canada',
        PR: 'Puerto Rico'
    };

    form: FormGroup;
    locationOptions: { value: string; label: string }[] = [];
    availableFields: { key: string; label: string; rank: number }[] = [];
    activeFields: string[] = [];
    fieldOptions: { [key: string]: string[] } = {};
    private labelMap: { [key: string]: string } = {};
    private rankMap: { [key: string]: number } = {};
    private activeFieldsSet = new Set<string>();
    isEditMode = false;
    dialogTitle = 'Create Assignment';

    constructor(
        public dialogRef: MatDialogRef<CreateOrEditAssignmentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: {locationId: string, mode?: 'create' | 'edit', assignment?: VariationAssignment},
        private cdr: ChangeDetectorRef,
        private itemDataService: ItemDataService,
    ) {
        this.isEditMode = data?.mode === 'edit';
        this.dialogTitle = this.isEditMode ? 'Edit Assignment' : 'Create Assignment';
    }

    ngOnInit() {
        this.buildForm();
        this.itemDataService.getAllowedAssignmentProperties().subscribe(propertyDetails => {
            if (propertyDetails && Array.isArray(propertyDetails)) {
                this.initializePropertyMappings(propertyDetails);
                if (this.isEditMode && this.getAssignment()) {
                    this.populateFormFromAssignment();
                }
                this.markForCheck();
            }
        });
    }

    private initializePropertyMappings(propertyDetails: any[]): void {
        const fields: { key: string; label: string; rank: number }[] = [];

        propertyDetails.forEach((property: any) => {
            const { propertyName: key, propertyRank: rank, availableValues = [] } = property;
            const label = this.formatPropertyLabel(key);

            fields.push({ key, label, rank });
            this.labelMap[key] = label;
            this.rankMap[key] = rank;
            this.fieldOptions[key] = availableValues;
        });

        this.availableFields = this.sortByRank(fields);
    }

    private getAssignment(): VariationAssignment | undefined {
        return this.data?.assignment;
    }

    private formatPropertyLabel(propertyName: string): string {
        return propertyName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    }

    private sortByRank<T extends { rank?: number } | string>(items: T[]): T[] {
        return items.slice().sort((a, b) => {
            const rankA = typeof a === 'string' ? (this.rankMap[a] || 0) : (a.rank || 0);
            const rankB = typeof b === 'string' ? (this.rankMap[b] || 0) : (b.rank || 0);
            return rankB - rankA;
        });
    }

    private markForCheck(): void {
        this.cdr.markForCheck();
    }

    private syncActiveFields(): void {
        this.activeFields = this.sortByRank(Array.from(this.activeFieldsSet));
    }

    private populateFormFromAssignment(): void {
        const assignment = this.getAssignment();
        if (!assignment?.properties) {
            return;
        }

        Object.entries(assignment.properties).forEach(([key, value]) => {
            this.addOrUpdateFormControl(key, value);
        });

        this.syncActiveFields();
        this.markForCheck();
    }

    private addOrUpdateFormControl(key: string, value: any): void {
        if (!this.activeFieldsSet.has(key)) {
            this.activeFieldsSet.add(key);
            this.form.addControl(key, new FormControl(value));
        } else {
            this.form.get(key)?.setValue(value);
        }
    }

    buildForm(): void {
        const assignment = this.getAssignment();
        const locationId = this.getLocationId(assignment);
        const consumer = this.getConsumer(assignment);

        this.locationOptions = this.buildLocationOptions(locationId);
        this.form = this.createFormGroup(locationId, consumer);
    }

    private getLocationId(assignment?: VariationAssignment): string {
        return assignment?.locationId || this.data?.locationId || CreateOrEditAssignmentDialogComponent.DEFAULT_LOCATION;
    }

    private getConsumer(assignment?: VariationAssignment): string {
        return assignment?.consumer || CreateOrEditAssignmentDialogComponent.DEFAULT_CONSUMER;
    }

    private buildLocationOptions(locationId: string): { value: string; label: string }[] {
        const options = [
            {
                value: CreateOrEditAssignmentDialogComponent.DEFAULT_LOCATION,
                label: CreateOrEditAssignmentDialogComponent.LOCATION_LABELS[CreateOrEditAssignmentDialogComponent.DEFAULT_LOCATION]
            }
        ];

        if (locationId !== CreateOrEditAssignmentDialogComponent.DEFAULT_LOCATION) {
            options.push({
                value: locationId,
                label: CreateOrEditAssignmentDialogComponent.LOCATION_LABELS[locationId] || locationId
            });
        }

        return options;
    }

    private createFormGroup(locationId: string, consumer: string): FormGroup {
        return new FormGroup({
            consumer: new FormControl({ value: consumer, disabled: true }),
            location: new FormControl({ value: locationId, disabled: true })
        });
    }

    updateFields(selectedFields: string[]): void {
        const nextSet = new Set<string>(selectedFields);

        this.addNewFields(selectedFields);
        this.removeObsoleteFields(nextSet);

        this.activeFieldsSet = nextSet;
        this.syncActiveFields();
        this.markForCheck();
    }

    private addNewFields(fields: string[]): void {
        fields.forEach(fieldKey => {
            if (!this.activeFieldsSet.has(fieldKey)) {
                this.form.addControl(fieldKey, new FormControl(''));
            }
        });
    }

    private removeObsoleteFields(nextSet: Set<string>): void {
        this.activeFieldsSet.forEach(fieldKey => {
            if (!nextSet.has(fieldKey)) {
                this.form.removeControl(fieldKey);
            }
        });
    }

    removeField(fieldKey: string): void {
        if (!this.activeFieldsSet.has(fieldKey)) {
            return;
        }

        this.activeFieldsSet.delete(fieldKey);
        this.form.removeControl(fieldKey);
        this.syncActiveFields();
        this.markForCheck();
    }

    getFieldLabel(fieldKey: string): string {
        return this.labelMap[fieldKey] || fieldKey;
    }

    getFieldOptions(fieldKey: string): string[] {
        return this.fieldOptions[fieldKey] || [];
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        if (this.form.valid) {
            this.dialogRef.close(this.form.getRawValue());
        }
    }
}
